import React, { useState } from 'react';
import {
    Button, Grid, TextField, MenuItem, Typography, CircularProgress
} from '@mui/material';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader, { capitalizeFirst, formatMonthYear } from '../components/PrintAgencyHeader';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// boubaker 23/08/2026 - Page d'impression des reçus de paiement
// (GenericPage type 'print' : filtres masquables + impression de la zone résultats)
export const PrintReceiptsPage: React.FC = () => {
    const { t } = useLanguage();

    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStart = todayStr.slice(0, 8) + '01';
    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo, setDateTo] = useState(todayStr);
    const [method, setMethod] = useState<'all' | 'Cash' | 'Card' | 'Transfer'>('all');

    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleLoad = async () => {
        setLoading(true);
        try {
            // boubaker 23/08/2026 - chargement serveur dédié impression (DLL Blobtic_Print : données JOINées + total)
            const res = await academyApi.getPrintReceipts(dateFrom, dateTo, method);
            setRows(res?.payments || []);
            setTotal(res?.total || 0);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.start_date')} value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.end_date')} value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField select fullWidth size="small" label={t('academy.print.col_method')} value={method}
                    onChange={(e) => setMethod(e.target.value as any)}>
                    <MenuItem value="all">{t('academy.print.method_all')}</MenuItem>
                    <MenuItem value="Cash">{t('academy.print.cash')}</MenuItem>
                    <MenuItem value="Card">{t('academy.print.card')}</MenuItem>
                    <MenuItem value="Transfer">{t('academy.print.transfer')}</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} md={1.5}>
                <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleLoad}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('academy.scheduler.load')}
                </Button>
            </Grid>
        </Grid>
    );

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties =>
        ({ border: '1px solid #999', padding: '3px 5px', textAlign: 'left', width, ...extra });

    const methodLabel = (m: string) => m === 'Cash' ? t('academy.print.cash')
        : m === 'Card' ? t('academy.print.card') : t('academy.print.transfer');

    return (
        <GenericPage
            title={t('academy.print.receipts')}
            icon={<ReceiptIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
        >
            {!loaded ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    {t('academy.scheduler.not_loaded')}
                </Typography>
            ) : (
                <>
                    {/* Titre "Reçus de Paiement Août 2026" dérivé de la plage chargée */}
                    <PrintAgencyHeader title={`${capitalizeFirst(t('academy.menu.prints.receipts'))} ${formatMonthYear(dateFrom)}`} />

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={cellStyle(90)}>{t('academy.print.col_receipt')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_date')}</th>
                                <th style={cellStyle()}>{t('academy.print.col_student')}</th>
                                <th style={cellStyle()}>{t('academy.print.col_invoice')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_method')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_amount')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={6} style={{ ...cellStyle(), textAlign: 'center' }}>{t('academy.print.no_data')}</td></tr>
                            ) : rows.map(p => (
                                    <tr key={p.payment_id ?? p.receipt_number}>
                                        <td style={cellStyle(90, { fontWeight: 'bold' })}>{p.receipt_number}</td>
                                        <td style={cellStyle(80)}>{String(p.payment_date || '').slice(0, 10)}</td>
                                        <td style={cellStyle()}>{p.student_name || ''}</td>
                                        <td style={cellStyle()}>{p.invoice_number || ''}</td>
                                        <td style={cellStyle(80)}>{methodLabel(p.method)}</td>
                                        <td style={cellStyle(80)}>{p.amount}</td>
                                    </tr>
                                ))}
                            {rows.length > 0 && (
                                <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                    <td colSpan={4} style={{ ...cellStyle(), textAlign: 'right' }}>{t('academy.print.total')}</td>
                                    <td style={cellStyle(80)} colSpan={2}>{total}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </>
            )}
        </GenericPage>
    );
};

export default PrintReceiptsPage;
